import { currencyFormatter } from "@fla/common";
import {
  CustomCheckbox,
  LearnMoreModal,
  RadioSelect,
  Select,
  SuperannuationJointSchema,
  SuperannuationProps,
  SuperannuationSplitSchema,
  Superannuation_InitialState,
  TextArea,
  TextInput,
  toast,
  useOptions
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { FC, Fragment } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: Superannuation_InitialState;
  onSubmit: (values: Superannuation_InitialState) => void;
}
const Step02: FC<IProps> = ({ formId, store, onSubmit }) => {
  const { initialDetails } = useSelector(selectConsentOrdersBase);

  const { ownerV1Options } = useOptions({ initialDetails });

  const { handleSubmit, control, setError } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: store.owner === "joint" ? yupResolver(SuperannuationJointSchema) : yupResolver(SuperannuationSplitSchema)
  });

  const isTransferredInPercentage =
    useWatch({
      control,
      name: "if_percentage"
    }) === "yes";

  const ifSplitFund =
    useWatch({
      control,
      name: "if_split_fund"
    }) === "yes";

  const checkAndSubmit = (values: SuperannuationProps) => {
    if (store.owner && values.transfer_to && store.owner !== "joint" && store.owner === values.transfer_to) {
      setError(
        "transfer_to",
        {
          type: "custom",
          message: "fund transfer to & fund owner shouldn't be the same"
        },
        { shouldFocus: true }
      );
      toast({
        type: "error",
        message: "Bad Input",
        // subText: "fund transfer to & fund owner shouldn't be the same",
        position: "top-right"
      });
    } else if (store.owner === "joint") {
      const payload = {
        // if_split_fund: "",
        transfer_to: "",
        if_percentage: "",
        transfer_percentage: "",
        transfer_amount: "",
        ...values
      };
      onSubmit(payload);
    } else {
      const payload = {
        // joint_description: "",
        spouse_receive_amount: "",
        user_receive_amount: "",
        ...values
      };
      onSubmit(payload);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(checkAndSubmit)}>
      {store.owner === "joint" ? (
        <Fragment>
          <TextArea
            title="How do wish to divide this jointly held self managed super fund?"
            control={control}
            name="joint_description"
            id="joint_description"
            placeholder="Description"
            subTitle={`The current balance is ${currencyFormatter.format(Number(store.value))}`}
          />
          <TextInput
            title={`How much will ${initialDetails?.spouse?.first_name} receive approximately?`}
            control={control}
            name="spouse_receive_amount"
            id="spouse_receive_amount"
            placeholder="eg $500,000"
            isAmount
          />
          <TextInput
            title={`How much will ${initialDetails?.user?.first_name} receive approximately?`}
            control={control}
            name="user_receive_amount"
            id="user_receive_amount"
            placeholder="eg $500,000"
            isAmount
          />
        </Fragment>
      ) : (
        <Fragment>
          <RadioSelect
            title="Do you wish to do a super split on this fund?"
            subTitle={
              <LearnMoreModal
                text="What’s a super split?"
                textClasses="font-normal"
                reverse
                title="What is super split?"
              >
                <p className="mt-5">
                  Super splitting is where person 1’s superannuation fund transfers a portion of person 1’s super to
                  person 2’s superannuation fund. Super splitting is available to all couples (including de facto
                  couples) in Australia. <br />
                  <br /> Remember, super cannot be accessed until retirement age (unless you apply for a financial
                  hardship withdraw).
                </p>
              </LearnMoreModal>
            }
            name="if_split_fund"
            id="if_split_fund"
            control={control}
            options={["yes", "no"]}
          />

          {ifSplitFund ? (
            <>
              <hr className="mt-7 mb-10 bg-gray-500 w-full" />
              <Select
                title="To whom will the transfer be made?"
                control={control}
                name="transfer_to"
                id="transfer_to"
                placeholder="Select"
                options={ownerV1Options}
              />
            </>
          ) : null}

          {ifSplitFund ? (
            <>
              <hr className="mt-7 mb-10 bg-gray-500 w-full" />

              {isTransferredInPercentage ? (
                <TextInput
                  title="What percentage of the super fund will be transferred to this person?"
                  control={control}
                  name="transfer_percentage"
                  id="transfer_percentage"
                  placeholder=""
                  isPercentage
                />
              ) : (
                <TextInput
                  title="How much is being transferred?"
                  control={control}
                  name="transfer_amount"
                  id="transfer_amount"
                  placeholder="eg $500,000"
                  isAmount
                />
              )}
              <CustomCheckbox
                control={control}
                name="if_percentage"
                title="Click to divide by percentage"
                valueType="string"
              />
            </>
          ) : null}
        </Fragment>
      )}
    </form>
  );
};
export default Step02;
